import {ReactComponent as MainLogo} from '../../assets/svg/logo/main.svg'
import PageHeading from "../../components/elements/PageHeading";
import MaskedInputField from "../../components/elements/MaskedInputField";
import ButtonWidget from "../../components/elements/ButtonWidget";
import {PagaStatus, usePagaStatus} from "../../contexts/PagaStatusContext";
import Authorization from "../auth/Authorization";
import PayWithApp from "./PayWithApp";
import PayWithPaga from "./PayWithPaga";
import PasswordAuthentication from "../auth/PasswordAuthentication";
import SuccessModal from "../SuccessModal";
import {useEffect, useState} from "react";
import FormError from "../../components/elements/FormError";
import {ErrorClass} from "../../constants/ErrorConstants";
import {mergeObjectsWithPriority, requestPayment} from "../../services/PaymentService";
import {FundingSourceType} from "../../constants/PaymentRequestConstants";
import {usePayment} from "../../contexts/PaymentContext";
import {useMerchant} from "../../contexts/MerchantContext";
import FailedAppPayment from "./failed/FailedAppPayment";
import OTPAuthentication from "../auth/OTPAuthentication";
import PinAuthentication from "../auth/PinAuthentication";
import {useAmplitudeEvents} from "../../components/elements/AmplitudeTracker";

const PAGA_ORANGE = '#f37244'
// Smart link: sends users to the App Store or Google Play based on their device
const PAGA_APP_DOWNLOAD_URL = 'https://smart.link/w6hy5vgnh50t6'
// Match these to MaskedInputField's input so the phone prefix lines up
const FIELD_BORDER = '#dcdcdc'
const PHONE_FIELD_HEIGHT = '38px'

export const PaymentMethod = {
    APP: 'APP',
    LOGIN: 'LOGIN',
}

export const IdentifierType = {
    PHONE: 'phone',
    EMAIL: 'email',
    USERNAME: 'username',
}

const IDENTIFIER_CONFIG = {
    [IdentifierType.PHONE]: {label: 'Phone number', switchLabel: 'Use phone number instead'},
    [IdentifierType.EMAIL]: {label: 'Email address', switchLabel: 'Use email instead', placeholder: 'name@example.com'},
    [IdentifierType.USERNAME]: {label: 'Username', switchLabel: 'Use username instead', placeholder: 'Your Paga username'},
}

const METHOD_CONFIG = {
    [PaymentMethod.APP]: {
        title: 'Pay with app',
        description: 'Get a payment request in your Paga app',
        identifierTypes: [IdentifierType.PHONE, IdentifierType.EMAIL],
        cta: 'Pay with app',
        color: undefined,
    },
    [PaymentMethod.LOGIN]: {
        title: 'Login to pay',
        description: 'Sign in to your Paga account',
        identifierTypes: [IdentifierType.PHONE, IdentifierType.EMAIL, IdentifierType.USERNAME],
        cta: 'Login to pay',
        color: PAGA_ORANGE,
    },
}

// National number lengths exclude the leading trunk 0. Extend as needed.
export const COUNTRIES = [
    {code: 'NG', name: 'Nigeria', dial: '234', min: 10, max: 10, placeholder: '806 234 5678'}
]
const DEFAULT_COUNTRY = 'NG'
const getCountry = (code) => COUNTRIES.find((c) => c.code === code) || COUNTRIES[0]

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
const E164_REGEX = /^\+[1-9]\d{7,14}$/
const USERNAME_REGEX = /^[a-zA-Z0-9_]{4,}$/

/**
 * Turns what the user typed into an identifier the API can use.
 * Phone numbers always come out in international format (+2348012345678).
 * Returns { type, value } or { error }.
 */
export const buildIdentifier = ({type, values, country}) => {
    const raw = (values[type] || '').trim()

    if (type === IdentifierType.PHONE) {
        if (!raw) return {error: 'Enter your phone number'}

        // Someone pasted a full international number: accept it as-is
        if (raw.startsWith('+')) {
            const full = '+' + raw.replace(/\D/g, '')
            return E164_REGEX.test(full)
                ? {type, value: full}
                : {error: 'Enter a valid phone number'}
        }

        const {dial, min, max, name} = getCountry(country)
        let national = raw.replace(/\D/g, '')
        // Typed the country code without "+", e.g. 2348012345678
        if (national.startsWith(dial) && national.length - dial.length >= min) {
            national = national.slice(dial.length)
        }
        // Drop the local trunk prefix, e.g. 08012345678 → 8012345678
        national = national.replace(/^0+/, '')

        return national.length >= min && national.length <= max
            ? {type, value: `+${dial}${national}`}
            : {error: `Enter a valid ${name} phone number`}
    }

    if (type === IdentifierType.EMAIL) {
        if (!raw) return {error: 'Enter your email address'}
        return EMAIL_REGEX.test(raw) ? {type, value: raw} : {error: 'Enter a valid email address'}
    }

    if (!raw) return {error: 'Enter your username'}
    return USERNAME_REGEX.test(raw)
        ? {type, value: raw}
        : {error: 'Usernames have at least 4 letters, numbers or underscores'}
}

const createEntry = (email = '') => ({
    type: IdentifierType.EMAIL,
    country: DEFAULT_COUNTRY,
    values: {[IdentifierType.PHONE]: '', [IdentifierType.EMAIL]: email, [IdentifierType.USERNAME]: ''},
})

const TotalSummary = ({feeDetails}) => (
    <div className="darker-grey-container mt-3 p-2">
        <div className="d-flex justify-content-between m-1">
            <p className="text-md grey-color">
                Pay a total of:
            </p>
            <div className="d-flex flex-column">
                <p className="text-xl justify-content-end fw-semibold">{'₦' + feeDetails.total}</p>
                <p className="text-sm grey-color justify-content-end">
                    {'Includes fee ₦' + feeDetails.amount}
                </p>
            </div>
        </div>
    </div>
)

const IdentifierInput = ({method, entry, onValueChange, onCountryChange, onBlur}) => {
    const {type, values, country} = entry
    const name = `loginInfo-${method.toLowerCase()}-${type}`

    if (type === IdentifierType.PHONE) {
        const selected = getCountry(country)
        return (
            <div className="text-start">
                <label className="text-sm d-block mb-1" htmlFor={name}>{IDENTIFIER_CONFIG[type].label}</label>
                <div className="d-flex align-items-start gap-2">
                    {/* Drawn prefix; the real <select> sits invisibly on top so the
                        native picker still opens and its arrow can't overlap the text */}
                    <div className="position-relative d-flex align-items-center rounded px-3 text-nowrap"
                         style={{border: `1px solid ${FIELD_BORDER}`, height: PHONE_FIELD_HEIGHT}}>
                        <span className="text-md">{`+${selected.dial}`}</span>
                        <span className="grey-color ms-2" aria-hidden="true"
                              style={{display: 'inline-block', transform: 'rotate(90deg)'}}>›</span>
                        <select className="position-absolute top-0 start-0 w-100 h-100"
                                style={{opacity: 0, cursor: 'pointer'}}
                                aria-label={`Country code, ${selected.name} +${selected.dial}`}
                                value={country}
                                onChange={(e) => onCountryChange(e.target.value)}>
                            {COUNTRIES.map((c) => (
                                <option key={c.code} value={c.code}>{`${c.name} (+${c.dial})`}</option>
                            ))}
                        </select>
                    </div>
                    <div className="flex-grow-1">
                        <MaskedInputField
                            mask={false}
                            guide={false}
                            placeholderChar={"\u2000"}
                            placeholder={selected.placeholder}
                            id={name}
                            name={name}
                            type="tel"
                            inputMode="tel"
                            autoComplete="tel-national"
                            value={values[type]}
                            required
                            autoFocus
                            onChange={(e) => onValueChange(e.target.value)}
                            onBlur={onBlur}
                        />
                    </div>
                </div>
            </div>
        )
    }

    return (
        <MaskedInputField
            key={type}
            mask={false}
            label={IDENTIFIER_CONFIG[type].label}
            guide={false}
            placeholderChar={"\u2000"}
            placeholder={IDENTIFIER_CONFIG[type].placeholder}
            name={name}
            type={type === IdentifierType.EMAIL ? 'email' : 'text'}
            autoComplete={type === IdentifierType.EMAIL ? 'email' : 'username'}
            value={values[type]}
            required
            autoFocus
            onChange={(e) => onValueChange(e.target.value)}
            onBlur={onBlur}
        />
    )
}

/**
 * One payment method: a header button that is always visible, plus an
 * input section that only renders while this method is selected.
 */
const MethodSection = ({
                           method, expanded, onToggle, entry, error, onTypeChange, onValueChange,
                           onCountryChange, onBlur, onSubmit, submitDisabled, loading,
                       }) => {
    const config = METHOD_CONFIG[method]
    const panelId = `paga-method-panel-${method.toLowerCase()}`
    const otherTypes = config.identifierTypes.filter((t) => t !== entry.type)

    return (
        <div className="rounded"
             style={{border: `1px solid ${expanded ? PAGA_ORANGE : FIELD_BORDER}`}}>
            <button type="button"
                    className="btn w-100 d-flex justify-content-between align-items-center p-3 border-0"
                    aria-expanded={expanded}
                    aria-controls={panelId}
                    onClick={() => onToggle(method)}>
                <span className="d-flex flex-column text-start">
                    <span className="text-md fw-semibold">{config.title}</span>
                    <span className="text-sm grey-color">{config.description}</span>
                </span>
                <span className="text-xl grey-color" aria-hidden="true"
                      style={{transform: expanded ? 'rotate(90deg)' : 'none'}}>›</span>
            </button>

            {expanded ?
                <div id={panelId} className="px-3 pb-3">
                    <IdentifierInput method={method}
                                     entry={entry}
                                     onValueChange={onValueChange}
                                     onCountryChange={onCountryChange}
                                     onBlur={onBlur}/>

                    {error ? <FormError className="mt-1">{error}</FormError> : null}

                    <div className="d-flex flex-wrap gap-3 mt-2">
                        {otherTypes.map((t) => (
                            <button key={t} type="button"
                                    className="btn btn-link p-0 text-sm text-decoration-none"
                                    style={{color: PAGA_ORANGE}}
                                    onClick={() => onTypeChange(t)}>
                                {IDENTIFIER_CONFIG[t].switchLabel}
                            </button>
                        ))}
                    </div>

                    <ButtonWidget className="modal-btn w-100 mt-3"
                                  color={config.color}
                                  disabled={submitDisabled}
                                  loading={loading}
                                  onClick={onSubmit}>
                        {config.cta}
                    </ButtonWidget>
                </div>
                : null}
        </div>
    )
}

const PagaPayment = () => {

    const {
        trackPaymentSubmitted,
        trackPaymentFailed
    } = useAmplitudeEvents()

    const {pagaStatus, showPayWithApp, setAppPaymentRequestedAt, selectedFundingSource, showSuccessfulPayment,
        loginInfo, setLoginInfo, setErrorMessage, showAuth, showPayWithPaga, appUser} = usePagaStatus()
    const [appButtonLoading, setAppButtonLoading] = useState(false)
    const [selectedMethod, setSelectedMethod] = useState(null)
    const {paymentParams} = usePayment()
    // Each method keeps its own identifier type, values and country
    const [entries, setEntries] = useState(() => ({
        [PaymentMethod.APP]: createEntry(paymentParams.email || ''),
        [PaymentMethod.LOGIN]: createEntry(paymentParams.email || ''),
    }))
    const [inputErrors, setInputErrors] = useState({[PaymentMethod.APP]: '', [PaymentMethod.LOGIN]: ''})
    const {getFeeDetailsByChannel} = useMerchant()
    const pagaFeeDetails = getFeeDetailsByChannel('PAGA')

    useEffect(() => {
        if (paymentParams.email) setLoginInfo(paymentParams.email)
        return () => {
            // reset error on unmounted
            setErrorMessage('')
        };
    }, []);

    const setInputError = (method, error) =>
        setInputErrors((prev) => ({...prev, [method]: error}))

    const updateEntry = (method, update) => {
        setErrorMessage('')
        setInputError(method, '')
        setEntries((prev) => ({...prev, [method]: update(prev[method])}))
    }

    const toggleMethod = (method) => {
        setErrorMessage('')
        setInputError(method, '')
        // Selecting the open method again collapses it; selecting the other swaps
        setSelectedMethod((current) => (current === method ? null : method))
    }

    const requestAppPayment = async (identifier) => {
        setAppButtonLoading(true)
        setErrorMessage(null)

        const userParameters = {
            "email": identifier.type === IdentifierType.EMAIL ? identifier.value : null,
            "phoneNumber": identifier.type === IdentifierType.PHONE ? identifier.value : null,
            "username": null,
        }

        trackPaymentSubmitted({ payment_method: "paga", payment_channel: "paga_app"});

        try {
            const res = await requestPayment(
                mergeObjectsWithPriority(userParameters, paymentParams)
                , FundingSourceType.APP, [], identifier.value)

            if (res.status === "PENDING") {
                setAppPaymentRequestedAt(new Date())
                showPayWithApp()
            }else{
                trackPaymentFailed({ payment_method: "paga", payment_channel: "paga_app", failure_reason: res.statusMessage || 'unknown'});
                setErrorMessage(res.statusMessage? res.statusMessage : ErrorClass.FAILED_INITIALIZATION)
            }
        } catch (err) {
            console.log(err)
            trackPaymentFailed({ payment_method: "paga", payment_channel: "paga_app", failure_reason: err.message || 'unknown'});
            setErrorMessage(ErrorClass.FAILED_INITIALIZATION)
        } finally {
            setAppButtonLoading(false)
        }
    }

    const handleBlur = (method) => () => {
        const entry = entries[method]
        // Only show an error once the user has typed something
        if (!entry.values[entry.type]) return
        setInputError(method, buildIdentifier(entry).error || '')
    }

    const handleSubmit = (method) => () => {
        const identifier = buildIdentifier(entries[method])
        if (identifier.error) {
            setInputError(method, identifier.error)
            return
        }

        setInputError(method, '')
        setLoginInfo(identifier.value)

        if (method === PaymentMethod.LOGIN) {
            showAuth()
        } else {
            requestAppPayment(identifier)
        }
    }

    const isSubmitDisabled = (method) => {
        if (appButtonLoading) return true
        const identifier = buildIdentifier(entries[method])
        // Stays disabled until the input passes validation (e.g. 10 digits for NG, a valid email)
        if (identifier.error) return true
        // Keep the existing rule: an app user can only pay with their own account
        if (method === PaymentMethod.APP && appUser) {
            return appUser !== identifier.value
        }
        return false
    }

    const renderMethod = (method) => (
        <MethodSection
            method={method}
            expanded={selectedMethod === method}
            onToggle={toggleMethod}
            entry={entries[method]}
            error={inputErrors[method]}
            onTypeChange={(type) => updateEntry(method, (e) => ({...e, type}))}
            onValueChange={(value) => updateEntry(method, (e) => ({...e, values: {...e.values, [e.type]: value}}))}
            onCountryChange={(country) => updateEntry(method, (e) => ({...e, country}))}
            onBlur={handleBlur(method)}
            onSubmit={handleSubmit(method)}
            submitDisabled={isSubmitDisabled(method)}
            loading={method === PaymentMethod.APP && appButtonLoading}
        />
    )

    return (
        <>
            {pagaStatus.status === PagaStatus.OTP ?
                <OTPAuthentication message={pagaStatus.message}
                                   fundingSourceType={selectedFundingSource}
                                   onBack={(message) => {
                                       showPayWithPaga(undefined, message)
                                   }}
                                   onSuccess={() => {
                                       showSuccessfulPayment()
                                   }}
                />
                : null}

            {pagaStatus.status === PagaStatus.PIN ?
                <PinAuthentication/> : null}

            {pagaStatus.status === PagaStatus.AUTH_SERVER ?
                <Authorization/> : null}

            {pagaStatus.status === PagaStatus.APP ?
                <PayWithApp/> : null}

            {pagaStatus.status === PagaStatus.AUTHENTICATED ?
                <PayWithPaga/> : null}

            {pagaStatus.status === PagaStatus.PASSWORD_AUTH ?
                <PasswordAuthentication/> : null}

            {pagaStatus.status === PagaStatus.FAILED_APP_PAYMENT ?
                <FailedAppPayment/> : null}

            {pagaStatus.status === PagaStatus.UNAUTHENTICATED ?
                <div className="box">
                    <div>
                        <PageHeading className="mb-3" title="" description="Pay with your Paga account">
                            <MainLogo className="mb-2"/>
                        </PageHeading>

                        {pagaStatus.message ?
                            <FormError className="mb-2">{pagaStatus.message}</FormError> : null}

                        <div className="d-flex flex-column gap-3">
                            {renderMethod(PaymentMethod.APP)}
                            {renderMethod(PaymentMethod.LOGIN)}
                        </div>

                        <TotalSummary feeDetails={pagaFeeDetails}/>
                    </div>

                    {/* Second child of .box, like the button row in the original layout,
                        so the box pushes it to the bottom */}
                    <div className="d-flex flex-column align-items-center mt-auto pt-3">
                        <span className="text-sm grey-color">Don’t have a Paga account?</span>
                        <a href={PAGA_APP_DOWNLOAD_URL}
                           target="_blank"
                           rel="noopener noreferrer"
                           className="text-sm text-decoration-none mt-1"
                           style={{color: PAGA_ORANGE}}>
                            Download the Paga app
                        </a>
                    </div>
                </div>
                : null}

            <SuccessModal show={pagaStatus.status === PagaStatus.SUCCESS} onHide={() => {}}
                          paymentDetails={{
                              refNo: paymentParams.payment_reference,
                              transactionType: 'Paga',
                              amount: paymentParams.amount,
                              fee: pagaFeeDetails.amount,
                              tax: pagaFeeDetails.taxes ? pagaFeeDetails.taxes.vat : null,
                              total: pagaFeeDetails.total,
                              extraDetails: {
                                  'Email': loginInfo
                              },
                          }}/>
        </>
    )
}

export default PagaPayment